import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Modal, Form } from 'react-bootstrap'
import TextInput from '../../../widgets/components/Input/TextInput'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import { useIntl } from 'react-intl'
import { Quill } from '../../../widgets/components/Input/Quill'
import { InputWithPhone } from '../../../widgets/components/Input/InputPhone'
import { ToolTipUIForModal } from '../../../widgets/components/UI/ToolTipUIForModal'
import brandingConfig from '../../../../../config/brandingConfig'
import * as Yup from 'yup'
import TagifyInput from '../../../shared-access/components/tagify-input'
import { CountryModel } from '../../../onboarding'
import { getCountry } from '../../../onboarding/core/_requests'
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js'
import { useAuth } from '../../../auth'
import { createFileRequest, editFileRequest } from '../../core/_requests'
import { toast } from 'react-toastify'
import { SelectInput } from '../../../widgets/components/Input/SelectInput'
import Skeleton from 'react-loading-skeleton'

const RequestFileModal = ({
  isModalActive,
  setIsModalActive,
  getAllFileRequest,
  requestedFileData,
  requestedFileDataLoading,
}: any) => {
  const { companyId, currentUser } = useAuth()
  const { formatMessage } = useIntl()
  const [phoneCodesOptions, setPhoneCodesOptions] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [quillText, setQuillText] = useState('')

  const initialValues = {
    DocumentName: requestedFileData?.name || '',
    Urgency: requestedFileData?.urgencyLevel || 'low',
    EmailTo: requestedFileData?.emailTo || '',
    EmailCc: requestedFileData?.emailCc || '',
    phone: (requestedFileData && requestedFileData?.whatsAppNumber.slice(-10)) || '',
    phoneCode: (requestedFileData && requestedFileData?.whatsAppNumber.slice(0, requestedFileData?.whatsAppNumber.length - 10)) || '',
  }

  useEffect(() => {
    const fetchCountry = async () => {
      const {
        data: { data: countries },
      } = await getCountry()

      const phoneCodesData = countries.map((country: CountryModel) => {
        return {
          dial_code: `+${country.phone_code}`,
          flag: `https://${process.env.REACT_APP_AWS_PUBLIC_ASSETS_BUCKET}.s3.${process.env.REACT_APP_REGION}.amazonaws.com/${country.flag_icon}`,
          code: country.country_code,
          name: country.country_name,
        }
      })
      setPhoneCodesOptions([...phoneCodesData])
    }
    fetchCountry()
  }, [isModalActive]) // eslint-disable-line react-hooks/exhaustive-deps

  const RequestFileSchema = Yup.object().shape({
    DocumentName: Yup.string().required(formatMessage({ id: 'Requested Document Name is required' })),
    Urgency: Yup.string().required(formatMessage({ id: 'Urgency Level is required' })),
    EmailTo: Yup.string()
      .required(formatMessage({ id: 'This field is required' }))
      .test('valid-emails', formatMessage({ id: 'One or more emails are invalid' }), (value) => {
        if (!value) return false

        const emails = value.split(',').map((email) => email.trim())
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        return emails.every((email) => emailRegex.test(email))
      }),
    EmailCc: Yup.string()
      .nullable()
      .test('valid-cc-emails', formatMessage({ id: 'One or more emails are invalid' }), (value) => {
        if (!value || value.trim() === '') return true
        const emails = value.split(',').map((email) => email.trim())
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        return emails.every((email) => emailRegex.test(email))
      }),
    phone: Yup.string()
      .nullable()
      .test('phone-number-valid', 'Invalid phone number', function (value, ctx: any) {
        if (!value || value === '') return true

        const selectedPhoneCode = ctx.parent.phoneCode
        const selectedCountryCode = phoneCodesOptions.find(
          (option: any) => option.dial_code === selectedPhoneCode
        )?.code

        if (phoneCodesOptions.length === 0 && ctx.originalValue) {
          return true
        }

        try {
          const phoneNumber = String(ctx.originalValue)
          const parsedNumber = parsePhoneNumberFromString(phoneNumber, selectedCountryCode)

          return isValidPhoneNumber(parsedNumber?.number as string)
        } catch (error) {
          return false
        }
      }),
  })

  const handleSubmit = async (values: any, { resetForm }: any) => {
    if (values.EmailCc.split(',').includes(currentUser?.email))
      return toast.error('You cannot add your email in CC field')
    if (!companyId) return
    setLoading(true)
    const payload = {
      name: values?.DocumentName,
      urgencyLevel: values?.Urgency,
      emailTo: values?.EmailTo,
      emailCc: values?.EmailCc,
      message: quillText,
      whatsAppNumber: values?.phoneCode + values?.phone,
    }
    try {
      if (!requestedFileData) {
        const {
          data: { success },
        } = await createFileRequest(companyId, payload)
        if (success) {
          toast.success('File request created successfully')
          resetForm()
          getAllFileRequest()
        }
      }
      else {
        const {
          data: { success },
        } = await editFileRequest(companyId, requestedFileData?.requestFileId, payload)
        if (success) {
          toast.success('File request edited successfully')
          resetForm()
          getAllFileRequest()
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
      setIsModalActive(false)
      setQuillText('')
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={RequestFileSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validateOnMount
      >
        {(formik: any) => {
          console.log(formik, 'formik')
          return (
            <Form>
              <Modal
                show={isModalActive}
                onHide={() => {
                  setIsModalActive(false)
                  setQuillText('')
                  formik.resetForm()
                }}
                fullscreen
              >
                <Modal.Header closeButton>
                  <div className='d-flex flex-column'>
                    <h2>{requestedFileData ? formatMessage({ id: 'Edit Request file' }) : formatMessage({ id: 'Request A File' })}</h2>
                  </div>
                </Modal.Header>
                <Modal.Body>
                  <div className='w-100 container'>
                    <InfoCard2
                      title={formatMessage({ id: 'How to Manage Your Cap Table Efficiently?' })}
                      desc={formatMessage({
                        id: `${brandingConfig.brandName}’s Cap Table Management feature offers a seamless and organized approach to track your equity, project future ownership scenarios, and analyze management events.`,
                      })}
                      slug='https://help.foundercrate.com/en/articles/9951400-how-to-publish-your-startup-for-voting-on-foundercrate'
                      className='w-100 mb-0'
                    />

                    <div className='row'>
                      <div className='col-md-6 col-12'>
                        {requestedFileDataLoading ?
                          <Skeleton
                            height={40}
                            count={1}
                            width={'75%'}
                            className='my-2'
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          /> :
                          <TextInput
                            fieldType='text'
                            label={formatMessage({ id: 'Requested Document Name' })}
                            fieldName='DocumentName'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Enter Requested Document Name' })}
                            toolTipText={formatMessage({
                              id: 'A unique name for this file request to help you easily identify and track its status in the RecordsKeeper system.',
                            })}
                            isStarRequired={true}
                            isTooltipForModal={true}
                            onBlur={formik.handleBlur}
                          />}
                      </div>
                      <div className='col-md-6 col-12'>
                        {requestedFileDataLoading ?
                          <Skeleton
                            height={40}
                            count={1}
                            width={'75%'}
                            className='my-2'
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          /> :
                          <SelectInput
                            label={formatMessage({ id: 'Urgency Level' })}
                            fieldName='Urgency'
                            placeholder={formatMessage({
                              id: 'Select Urgency Level',
                            })}
                            formik={formik}
                            toolTipText={formatMessage({
                              id: 'Select the urgency level for this request to indicate how quickly the document is needed.',
                            })}
                            options={['HIGH', 'MEDIUM', 'LOW']}
                            isTooltipForModal={true}
                          />}
                      </div>
                      <div className='mb-7'>
                        {requestedFileDataLoading ?
                          <Skeleton
                            height={300}
                            count={1}
                            width={'100%'}
                            className='my-2'
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          /> :
                          <>
                            <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                              {formatMessage({ id: 'Message to User' })}*{' '}
                              <ToolTipUIForModal
                                tooltipText={formatMessage({
                                  id: 'A personalized message to provide context and additional details about this document request to the recipient.',
                                })}
                              />
                            </label>
                            <Quill quillText={quillText} setQuillText={setQuillText} aiEmailResponse={requestedFileData?.message} />
                          </>
                        }
                      </div>
                      <div className='col-md-6 col-12 mb-5'>
                        {requestedFileDataLoading ?
                          <Skeleton
                            height={40}
                            count={1}
                            width={'75%'}
                            className='my-2'
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          /> :
                          <TagifyInput
                            placeholder={formatMessage({ id: 'Email to' })}
                            tooltip={formatMessage({
                              id: 'The email address(es), separated by comma, where the document request will be sent, including a link for the recipient to securely upload the requested file.',
                            })}
                            label={formatMessage({ id: 'Email to*' })}
                            fieldName='EmailTo'
                            formik={formik}
                            onBlur={formik.handleBlur}
                          />
                        }
                      </div>
                      <div className='col-md-6 col-12 mb-3'>
                        {requestedFileDataLoading ?
                          <Skeleton
                            height={40}
                            count={1}
                            width={'75%'}
                            className='my-2'
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          /> :
                          <TagifyInput
                            placeholder={formatMessage({ id: 'CC to' })}
                            tooltip={
                              'Email address(es), separated by comma, to include in the CC field, allowing others to receive a copy of this document request.'
                            }
                            label={formatMessage({ id: 'CC to' })}
                            fieldName='EmailCc'
                            formik={formik}
                          />}
                      </div>
                      <div className='col-md-6 col-12 mt-3'>
                        {requestedFileDataLoading ?
                          <Skeleton
                            height={40}
                            count={1}
                            width={'75%'}
                            className='my-2'
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          /> :
                          <InputWithPhone
                            label={formatMessage({ id: 'WhatsApp Number' })}
                            toolTipText={formatMessage({
                              id: 'Enter the WhatsApp number to also send this request via WhatsApp, providing the recipient with a link to upload the requested document.',
                            })}
                            isTooltipForModal={true}
                            fieldName='phone'
                            placeholder={formatMessage({ id: 'Enter Whatsapp Number' })}
                            placeholderSelect='Select'
                            formik={formik}
                            options={phoneCodesOptions}
                            selectOptionFieldName='phoneCode'
                          />
                        }
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer className='px-0 container'>
                  <div className='d-md-flex w-100 w-md-auto justify-content-end px-md-0 px-0 gap-3'>
                    <div className='w-100 order-1 order-md-2 mb-2 mb-md-auto'>
                      <button
                        type='submit'
                        className='btn bg-primary w-100 custom-button-property text-white'
                        disabled={!formik.isValid || loading || requestedFileDataLoading}
                        onClick={formik.submitForm}
                      >
                        {!loading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Send Request' })}
                          </span>
                        ) : (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </div>
                    <div className='w-100 w-md-auto order-2 order-md-1 '>
                      <button
                        type='button'
                        className='btn bg-light text-muted custom-button-property w-100'
                        onClick={() => {
                          setIsModalActive(false)
                          formik.resetForm()
                        }}
                      >
                        {formatMessage({ id: 'Cancel' })}
                      </button>
                    </div>
                  </div>
                </Modal.Footer>
              </Modal>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default RequestFileModal
